/**
 * Structured Logger for Authentication Flows
 * Provides timestamped, categorized logging with performance optimization
 */

export interface LogLevel {
  DEBUG: 0;
  INFO: 1;
  WARN: 2;
  ERROR: 3;
}

export const LOG_LEVELS: LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
} as const;

export type LogLevelType = keyof LogLevel;

export interface LogEntry {
  timestamp: string;
  level: LogLevelType;
  event: string;
  category: string;
  message: string;
  data?: any;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string;
  };
  sessionId?: string;
  userId?: string;
  duration?: number;
  metadata?: Record<string, any>;
}

export interface AuthLoggerConfig {
  level: LogLevelType;
  enableConsole: boolean;
  enableFile: boolean;
  maxEntries: number;
  includeSensitiveData: boolean;
  performanceMode: boolean;
}

class AuthLogger {
  private config: AuthLoggerConfig;
  private logs: LogEntry[] = [];
  private sessionId: string;
  private performanceTimers: Map<string, number> = new Map();

  constructor(config?: Partial<AuthLoggerConfig>) {
    this.config = {
      level: 'INFO',
      enableConsole: true,
      enableFile: false,
      maxEntries: 1000,
      includeSensitiveData: false,
      performanceMode: true,
      ...config,
    };

    this.sessionId = this.generateSessionId();
    
    // Initialize session
    this.info('AUTH_SESSION_STARTED', 'session', 'Authentication session initialized', {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
    });
  }

  private generateSessionId(): string {
    return `auth_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private shouldLog(level: LogLevelType): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private sanitizeData(data: any): any {
    if (!data) return data;
    
    // If not including sensitive data, redact common sensitive fields
    if (!this.config.includeSensitiveData && typeof data === 'object') {
      const sanitized = { ...data };
      const sensitiveFields = ['password', 'token', 'secret', 'key', 'credential'];
      
      for (const field of sensitiveFields) {
        if (sanitized[field]) {
          sanitized[field] = '[REDACTED]';
        }
      }
      
      return sanitized;
    }
    
    return data;
  }

  private createLogEntry(
    level: LogLevelType,
    event: string,
    category: string,
    message: string,
    data?: any,
    error?: Error | any
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      event,
      category,
      message,
      sessionId: this.sessionId,
    };

    if (data) {
      entry.data = this.sanitizeData(data);
    }

    if (error) {
      entry.error = {
        name: error.name || 'Error',
        message: error.message || 'Unknown error',
        stack: error.stack,
        code: error.code || error.error_code,
      };
    }

    // Add performance timing if available
    const timerKey = `${category}_${event}`;
    if (this.performanceTimers.has(timerKey)) {
      entry.duration = Date.now() - this.performanceTimers.get(timerKey)!;
      this.performanceTimers.delete(timerKey);
    }

    return entry;
  }

  private writeLog(entry: LogEntry): void {
    // Store in memory with size limit
    this.logs.push(entry);
    if (this.logs.length > this.config.maxEntries) {
      this.logs = this.logs.slice(-this.config.maxEntries);
    }

    // Console output
    if (this.config.enableConsole) {
      const consoleMessage = `[${entry.timestamp}] [${entry.level}] [${entry.category}] ${entry.event}: ${entry.message}`;
      
      switch (entry.level) {
        case 'DEBUG':
          console.debug(consoleMessage, entry.data || '');
          break;
        case 'INFO':
          console.log(consoleMessage, entry.data || '');
          break;
        case 'WARN':
          console.warn(consoleMessage, entry.data || '', entry.error || '');
          break;
        case 'ERROR':
          console.error(consoleMessage, entry.data || '', entry.error || '');
          break;
      }
    }

    // File output (if in Node.js environment)
    if (this.config.enableFile && typeof require !== 'undefined') {
      try {
        const fs = require('fs');
        const path = require('path');
        const logFile = path.join(process.cwd(), 'auth-logs.json');
        
        // Append to file (simple implementation)
        fs.appendFileSync(logFile, JSON.stringify(entry) + '\n');
      } catch (error) {
        console.warn('Failed to write log to file:', error);
      }
    }
  }

  // Performance timing methods
  startTimer(category: string, event: string): void {
    if (this.config.performanceMode) {
      const timerKey = `${category}_${event}`;
      this.performanceTimers.set(timerKey, Date.now());
    }
  }

  endTimer(category: string, event: string): number | undefined {
    if (this.config.performanceMode) {
      const timerKey = `${category}_${event}`;
      const startTime = this.performanceTimers.get(timerKey);
      if (startTime) {
        const duration = Date.now() - startTime;
        this.performanceTimers.delete(timerKey);
        return duration;
      }
    }
    return undefined;
  }

  // Logging methods
  debug(event: string, category: string, message: string, data?: any): void {
    if (this.shouldLog('DEBUG')) {
      const entry = this.createLogEntry('DEBUG', event, category, message, data);
      this.writeLog(entry);
    }
  }

  info(event: string, category: string, message: string, data?: any): void {
    if (this.shouldLog('INFO')) {
      const entry = this.createLogEntry('INFO', event, category, message, data);
      this.writeLog(entry);
    }
  }

  warn(event: string, category: string, message: string, data?: any, error?: Error): void {
    if (this.shouldLog('WARN')) {
      const entry = this.createLogEntry('WARN', event, category, message, data, error);
      this.writeLog(entry);
    }
  }

  error(event: string, category: string, message: string, data?: any, error?: Error): void {
    if (this.shouldLog('ERROR')) {
      const entry = this.createLogEntry('ERROR', event, category, message, data, error);
      this.writeLog(entry);
    }
  }

  // Auth-specific convenience methods
  authEvent(event: string, message: string, data?: any): void {
    this.info(event, 'auth', message, data);
  }

  authError(event: string, message: string, error?: Error, data?: any): void {
    this.error(event, 'auth', message, data, error);
  }

  verificationEvent(event: string, message: string, data?: any): void {
    this.info(event, 'verification', message, data);
  }

  verificationError(event: string, message: string, error?: Error, data?: any): void {
    this.error(event, 'verification', message, data, error);
  }

  sessionEvent(event: string, message: string, data?: any): void {
    this.info(event, 'session', message, data);
  }

  // Utility methods
  getLogs(category?: string, level?: LogLevelType): LogEntry[] {
    let filteredLogs = this.logs;
    
    if (category) {
      filteredLogs = filteredLogs.filter(log => log.category === category);
    }
    
    if (level) {
      filteredLogs = filteredLogs.filter(log => LOG_LEVELS[log.level] >= LOG_LEVELS[level]);
    }
    
    return filteredLogs;
  }

  clearLogs(): void {
    this.logs = [];
    this.info('LOGS_CLEARED', 'system', 'Authentication logs cleared');
  }

  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  getSessionSummary(): {
    sessionId: string;
    startTime: string;
    totalEvents: number;
    errorCount: number;
    warningCount: number;
    categories: string[];
    avgDuration?: number;
  } {
    const errorCount = this.logs.filter(log => log.level === 'ERROR').length;
    const warningCount = this.logs.filter(log => log.level === 'WARN').length;
    const categories = [...new Set(this.logs.map(log => log.category))];
    const durationsWithValues = this.logs.filter(log => log.duration !== undefined).map(log => log.duration!);
    const avgDuration = durationsWithValues.length > 0 
      ? durationsWithValues.reduce((sum, d) => sum + d, 0) / durationsWithValues.length 
      : undefined;

    return {
      sessionId: this.sessionId,
      startTime: this.logs[0]?.timestamp || new Date().toISOString(),
      totalEvents: this.logs.length,
      errorCount,
      warningCount,
      categories,
      avgDuration,
    };
  }
}

// Create singleton instance
export const authLogger = new AuthLogger({
  level: process.env.NODE_ENV === 'development' ? 'DEBUG' : 'INFO',
  enableConsole: true,
  enableFile: process.env.NODE_ENV === 'production',
  includeSensitiveData: process.env.NODE_ENV === 'development',
  performanceMode: true,
});

// Export types and logger
export { AuthLogger };